import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  IconButton,
  Button,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  CalendarToday,
  AttachMoney,
  People,
  Star,
  MoreVert,
  Refresh,
  Schedule,
  CheckCircle,
  Cancel,
  Store,
  VerifiedUser
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';
import { useNotification } from '../../context/NotificationContext';

const StatCard = ({ title, value, change, icon, color = 'primary' }) => {
  const isPositive = change >= 0;
  
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography color="text.secondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" component="div" fontWeight="bold">
              {value}
            </Typography>
            <Box display="flex" alignItems="center" mt={1}>
              {isPositive ? (
                <TrendingUp color="success" fontSize="small" />
              ) : (
                <TrendingDown color="error" fontSize="small" />
              )}
              <Typography
                variant="body2"
                color={isPositive ? 'success.main' : 'error.main'}
                sx={{ ml: 0.5 }}
              >
                {isPositive ? '+' : ''}{change}%
              </Typography>
            </Box>
          </Box>
          <Avatar
            sx={{
              bgcolor: `${color}.main`,
              width: 56,
              height: 56,
            }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
};

const Dashboard = () => {
  const { showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    totalRevenue: 0,
    totalBookings: 0,
    totalCustomers: 0,
    totalSalons: 0,
    activeSalons: 0,
    verifiedSalons: 0,
    averageRating: 0,
    revenueChange: 0,
    bookingsChange: 0,
    customersChange: 0,
    ratingChange: 0,
    recentBookings: []
  });
  const [recentBookings, setRecentBookings] = useState([]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getDashboard();
      
      if (response.success && response.data) {
        const data = response.data;
        
        // Set dashboard statistics
        setDashboardData({
          totalRevenue: data.revenue?.total || 0,
          totalBookings: data.appointments?.total || 0,
          totalCustomers: data.users?.total || 0,
          totalSalons: data.salons?.total || 0,
          activeSalons: data.salons?.active || 0,
          verifiedSalons: data.salons?.verified || 0,
          averageRating: data.reviews?.averageRating || 0,
          revenueChange: data.revenue?.thisMonth || 0,
          bookingsChange: data.appointments?.today || 0,
          customersChange: data.users?.newThisMonth || 0,
          ratingChange: 0,
        });

        // Set recent activity data
        if (data.recentActivity?.appointments) {
          const formattedBookings = data.recentActivity.appointments.map(appointment => ({
            id: appointment._id,
            customerName: `${appointment.client?.firstName || ''} ${appointment.client?.lastName || ''}`.trim(),
            service: appointment.service?.name || 'Unknown Service',
            time: new Date(appointment.appointmentDate).toLocaleTimeString('en-US', { 
              hour: 'numeric', 
              minute: '2-digit',
              hour12: true 
            }),
            status: appointment.status,
            avatar: null,
          }));
          setRecentBookings(formattedBookings);
        }
      }
    } catch (error) {
      console.error('Dashboard data loading error:', error);
      showError('Failed to load dashboard data');
      // Fallback to default values on error
      setDashboardData({
        totalRevenue: 0,
        totalBookings: 0,
        totalCustomers: 0,
        averageRating: 0,
        revenueChange: 0,
        bookingsChange: 0,
        customersChange: 0,
        ratingChange: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'completed':
        return 'info';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle fontSize="small" />;
      case 'pending':
        return <Schedule fontSize="small" />;
      case 'completed':
        return <CheckCircle fontSize="small" />;
      case 'cancelled':
        return <Cancel fontSize="small" />;
      default:
        return <Schedule fontSize="small" />;
    }
  };

  return (
    <Box>
      {loading && <LinearProgress />}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Dashboard Overview
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadDashboardData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Revenue"
            value={`₹${dashboardData.totalRevenue}`}
            change={dashboardData.revenueChange}
            icon={<AttachMoney />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Bookings"
            value={dashboardData.totalBookings}
            change={dashboardData.bookingsChange}
            icon={<CalendarToday />}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Customers"
            value={dashboardData.totalCustomers}
            change={dashboardData.customersChange}
            icon={<People />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Average Rating"
            value={dashboardData.averageRating}
            change={dashboardData.ratingChange}
            icon={<Star />}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Salon Statistics */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Salons"
            value={dashboardData.totalSalons}
            icon={<Store />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Active Salons"
            value={dashboardData.activeSalons}
            icon={<CheckCircle />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Verified Salons"
            value={dashboardData.verifiedSalons}
            icon={<VerifiedUser />}
            color="info"
          />
        </Grid>
      </Grid>

      {/* Recent Bookings */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Today's Bookings
            </Typography>
            <IconButton>
              <MoreVert />
            </IconButton>
          </Box>
          <List>
            {recentBookings.map((booking, index) => (
              <React.Fragment key={booking.id}>
                <ListItem
                  sx={{
                    px: 0,
                    '&:hover': {
                      bgcolor: 'action.hover',
                      borderRadius: 1,
                    },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar src={booking.avatar}>
                      {booking.customerName.charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle1" fontWeight="medium">
                          {booking.customerName}
                        </Typography>
                        <Chip
                          label={booking.status}
                          color={getStatusColor(booking.status)}
                          size="small"
                          icon={getStatusIcon(booking.status)}
                        />
                      </Box>
                    }
                    secondary={
                      <Box display="flex" justifyContent="space-between" mt={0.5}>
                        <Typography variant="body2" color="text.secondary">
                          {booking.service}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {booking.time}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                {index < recentBookings.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Dashboard;